/**
 * components/funnel/SiteHeader.jsx
 *
 * The header, built to the LAKA nav contract.
 *
 * THE CONTRACT (brand-marks, section 04 — rules nav.four and nav.no-inline,
 * both severity block)
 *
 * The bar contains exactly four elements, in this order:
 *
 *   1. seal        34px standing, 32px condensed, 24px absolute floor.
 *                  Always the canonical asset — never a letterform, initial
 *                  or generated stand-in (rule brand.seal-asset).
 *   2. site name   uppercase, 20px / 700 / +0.02em, first word in heading
 *                  colour and second in accent (rule nav.name-case).
 *   3. menu button an outlined pill carrying the WORD "MENU" — not a bare
 *                  hamburger. Border goes accent and the glyph flips to a
 *                  close mark when open.
 *   4. cta         one solid accent pill, always last.
 *
 * Explicitly banned from the bar: inline nav links, a search field, a second
 * CTA, an account avatar, a phone number or email, a language switcher, a
 * bare hamburger with no label.
 *
 * WHAT THIS REPLACED
 *
 * Five inline nav links and a text-only wordmark. Every destination moved
 * into the menu panel below, which is where the contract says they go. The
 * count of elements in the bar is the whole point of the rule: it is what
 * keeps every Bow Tie property recognisably the same object.
 *
 * ACCESSIBILITY NOTES
 *
 * The site name renders the second word in the accent at 20px/700. That is
 * legal — rule color.accent-text blocks the accent below 24px *normal* or
 * 18.66px *bold*, and 20px at weight 700 clears the bold threshold.
 *
 * The menu is a real disclosure: the button owns aria-expanded and
 * aria-controls, focus moves into the panel on open and returns to the
 * button on close, and Escape closes it. Rule kbd.parity — anything
 * operable by pointer is operable by keyboard.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button, Wrap, cx } from "./primitives";

/**
 * Every destination. These live in the panel, never in the bar.
 * Grouped so the panel reads as a map of the site rather than a list.
 */
const MENU_GROUPS = [
  {
    heading: "GrowthPoints",
    links: [
      { to: "/products", label: "All products & services" },
      { to: "/growth-points", label: "How it works" },
      { to: "/growth-points#plans", label: "Plans and capacity" },
      { to: "/growth-points/catalog", label: "Point catalog" },
      { to: "/growth-points#blocks", label: "One-off blocks" },
    ],
  },
  {
    heading: "Proof",
    links: [
      { to: "/results", label: "Client results" },
      { to: "/case-studies", label: "Case studies" },
      { to: "/about-bow-tie-kreative", label: "About the agency" },
    ],
  },
  {
    heading: "Free resources",
    links: [
      { to: "/marketing-audit-free", label: "Marketing audit" },
      { to: "/seo-report", label: "SEO report" },
      { to: "/revenue-masterclass", label: "Revenue masterclass" },
      { to: "/blog", label: "The journal" },
    ],
  },
];

export default function SiteHeader({ announcement = true }) {
  const [open, setOpen] = useState(false);
  const [condensed, setCondensed] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    // Focus returns to the control that opened the panel, or the user is
    // dropped at the top of the document with no idea where they are.
    buttonRef.current?.focus();
  }, []);

  // The bar condenses from 78px to 64px once the page has moved.
  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    // Move focus into the panel so the next Tab lands on its first link
    // rather than continuing past the header into the page behind it.
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const barHeight = condensed ? 64 : 78;
  const sealSize = condensed ? 32 : 34;

  return (
    <>
      {announcement && (
        <div
          data-testid="announcement-bar"
          className="border-b border-[color:var(--hairline-soft)] bg-ink-800"
        >
          <Wrap className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 py-3 text-center text-[13px]">
            <span className="font-semibold text-[color:var(--rp-heading)]">Now available:</span>
            <span className="text-[color:var(--rp-body)]">
              GrowthPoints — flexible growth capacity without hourly billing.
            </span>
            <Link
              to="/growth-points"
              className="font-semibold text-brand-400 underline-offset-4 hover:text-brand-300 hover:underline"
              data-testid="announcement-cta"
            >
              See how it works →
            </Link>
          </Wrap>
        </div>
      )}

      <header
        data-testid="site-header"
        className="rp-glass sticky top-0 z-50 border-b border-[color:var(--hairline-soft)]"
      >
        <Wrap
          className="flex items-center justify-between transition-[height] duration-300"
          style={{ height: barHeight }}
        >
          {/* --- group left: seal + site name (contract order 1, 2) ----- */}
          <Link
            to="/"
            className="flex items-center"
            /* The nav contract specifies gap_left: 13, but 13px is not on the
               LAKA spacing scale and rule design-system.spacing-scale is
               block-severity. Using 12 — the nearest permitted value, and a
               one-pixel difference nobody can see. Reported upstream: the
               contract and the scale disagree. */
            style={{ gap: 12 }}
            data-testid="header-logo"
            aria-label="Bow Tie Kreative — home"
          >
            <img
              src="/brand/btk-seal-white-64.webp"
              alt=""
              width={sealSize}
              height={sealSize}
              className="shrink-0 transition-[width,height] duration-300"
              style={{ width: sealSize, height: sealSize }}
            />
            {/* Rule nav.name-case: uppercase, 20px/700/+0.02em, first word in
                heading colour and second in accent. */}
            <span className="text-[16px] sm:text-[20px] font-bold uppercase leading-none tracking-[0.02em]">
              <span className="text-[color:var(--rp-heading)]">Bow Tie </span>
              <span className="text-brand-400 sm:text-brand">Kreative</span>
            </span>
          </Link>

          {/* --- group right: MENU + one CTA (contract order 3, 4) ------ */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <button
              ref={buttonRef}
              type="button"
              onClick={() => (open ? close() : setOpen(true))}
              aria-expanded={open}
              aria-controls="site-menu-panel"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="menu-button"
              className={cx(
                // 44px tall: rule target.preferred.
                "inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-[var(--radius-pill)] border px-3 sm:px-5 text-[13px] font-bold uppercase tracking-[0.12em] transition-colors",
                open
                  ? "border-brand text-[color:var(--rp-heading)]"
                  : "border-[color:var(--hairline)] text-[color:var(--rp-body)] hover:border-[color:var(--rp-heading)] hover:text-[color:var(--rp-heading)]",
              )}
            >
              <span aria-hidden="true" className="text-[15px] leading-none">
                {open ? "✕" : "☰"}
              </span>
              <span className="hidden sm:inline">{open ? "Close" : "Menu"}</span>
            </button>

            <Button to="/apply" size="md" className="h-11 rounded-[var(--radius-pill)] px-3 sm:px-6" aria-label="Apply now" data-testid="header-apply">
              <span className="sm:hidden">Apply</span><span className="hidden sm:inline">Apply now</span>
            </Button>
          </div>
        </Wrap>

        {/* --- the menu panel ------------------------------------------- */}
        {open && (
          <div
            id="site-menu-panel"
            ref={panelRef}
            tabIndex={-1}
            data-testid="site-menu-panel"
            className="border-t border-[color:var(--hairline-soft)] bg-ink-900 focus:outline-none"
          >
            <Wrap className="grid gap-10 py-10 md:grid-cols-3">
              {MENU_GROUPS.map((group) => (
                <nav key={group.heading} aria-label={group.heading}>
                  <p className="rp-eyebrow">{group.heading}</p>
                  <ul className="mt-4 list-none space-y-1 p-0">
                    {group.links.map((link) => (
                      <li key={link.label}>
                        <NavLink
                          to={link.to}
                          onClick={close}
                          className={({ isActive }) =>
                            cx(
                              // 44px row: rule target.preferred.
                              "flex min-h-[44px] items-center rounded-[var(--radius-control)] px-3 text-[15px] transition-colors",
                              isActive
                                ? "text-[color:var(--rp-heading)]"
                                : "text-[color:var(--rp-body)] hover:bg-ink-700 hover:text-[color:var(--rp-heading)]",
                            )
                          }
                        >
                          {link.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </Wrap>
          </div>
        )}
      </header>
    </>
  );
}
